import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  map,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import { uiActions } from '.';
import { VendorsService } from '../../services/vendors.service';
import { concat, defer, EMPTY, Observable, of } from 'rxjs';
import { ApiError } from '../../models/common/api-error';
import { storeUiKey } from '../reducers';
import { UiState } from './ui.state';
import { ServerRenderedDataService } from '../../services/server-rendered-data.service';

@Injectable({
  providedIn: 'root',
})
export class UiEffects {
  private serverRenderedData = inject(ServerRenderedDataService);

  constructor(
    private store$: Store,
    private actions$: Actions,
    private vendorsService: VendorsService,
  ) {}

  findOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uiActions.setVendorSlug),
      withLatestFrom(this.store$),
      // Déduplication : si le vendor demandé est déjà chargé, on ne refait pas l'appel
      filter(([action, store]) => {
        const uiState: UiState = store[storeUiKey];
        return action.vendorSlug !== uiState.vendor?.slug;
      }),
      mergeMap(([action, store]) => {
        if (action.vendorSlug) {
          return concat(
            this.findVendor(action.vendorSlug),
            this.reloadVendorWithSession(action.vendorSlug),
          );
        } else {
          return of(uiActions.setVendor({ vendor: null }));
        }
      }),
    ),
  );

  private findVendor(vendorSlug: string): Observable<Action> {
    return this.vendorsService.findOne(vendorSlug).pipe(
      map((response) => uiActions.setVendor({ vendor: response })),
      catchError((error) =>
        of(uiActions.setVendorError({ exception: error as ApiError })),
      ),
    );
  }

  private reloadVendorWithSession(vendorSlug: string): Observable<Action> {
    return defer(() => {
      if (!this.serverRenderedData.mustReloadWithSession()) {
        return EMPTY;
      }

      return this.vendorsService
        .findOne(vendorSlug, { ignoreServerRenderedResponse: true })
        .pipe(
          map((response) => uiActions.setVendor({ vendor: response })),
          catchError(() => EMPTY),
        );
    });
  }
}
